import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { PermissionGuard, useHasPermission } from "@/common/acl/guard/guard";
import { Dialog } from "@/common/components/dialog/dialog";
import { Role } from "@/common/services/user";
import { invariant } from "@/common/utils/invariant";
import { CompanySimpleUserToApprovalState } from "@/generated/api/users";

import { Table } from "../components/table/table";
import type { User as TUser } from "../components/user/user";
import {
  useConfirmedAction,
  User as UserComponent,
} from "../components/user/user";
import { useDeleteUserMutation } from "../components/user/user.api";
import { UserCompanyAction } from "../components/user-actions/user-company-action";
import { UserRoleAction } from "../components/user-actions/user-role-action";
import { UserStateAction } from "../components/user-actions/user-state-action";
import { AssignCompanyForm } from "./components/assign-company-form";
import { useBlockUserMutation, userQuery } from "./user.api";

const User = () => {
  const { id } = useParams();
  // eslint-disable-next-line lingui/no-unlocalized-strings
  invariant(id, "user id is missing");
  const user = useSuspenseQuery(
    userQuery({ ssoUserId: encodeURIComponent(id) }),
  );
  const data: TUser = {
    email: user.data.email,
    metadata: user.data.userMetadata,
    name: user.data.name,
    phone: user.data.phone,
    ssoUserId: user.data.id,
  };

  return (
    <UserComponent user={data}>
      <DangerZone user={{ ...data, blocked: user.data.isBlocked }} />
      <Companies
        companies={user.data.company.map((company) => ({
          approvalState: company.approvalState,
          name: company.companyName ?? "",
          role: company.role,
          userCompanyRelationId: company.id,
        }))}
      />
      <AssignCompanyForm userId={id} />
    </UserComponent>
  );
};

type Company = {
  userCompanyRelationId: number;
  name: string;
  approvalState: CompanySimpleUserToApprovalState;
  role: Role;
};

const columnHelper = createColumnHelper<Company>();

const Companies = ({ companies }: { companies: Array<Company> }) => {
  const hasCompanyApprovePermission = useHasPermission("users:company:approve");
  const hasUserCompanyDeletePermission = useHasPermission(
    "users:company:delete",
  );
  const hasCompanyRolePermission = useHasPermission("users:company:role");
  const columns = React.useMemo(
    () => [
      columnHelper.accessor("userCompanyRelationId", {
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("name", {
        cell: (info) => info.getValue(),
        header: () => t`Společnost`,
      }),
      columnHelper.accessor("approvalState", {}),
      columnHelper.accessor("role", {}),
      columnHelper.display({
        cell: hasCompanyApprovePermission
          ? ({ row }) => (
              <UserStateAction
                initialStatus={row.getValue("approvalState")}
                userCompanyRelationId={row.getValue("userCompanyRelationId")}
              />
            )
          : ({ row }) => row.getValue("approvalState"),
        header: () => t`Stav`,
        id: "state-action",
      }),
      columnHelper.display({
        cell: hasCompanyRolePermission
          ? ({ row }) => (
              <UserRoleAction
                initialRole={row.getValue("role")}
                userCompanyRelationId={row.getValue("userCompanyRelationId")}
              />
            )
          : ({ row }) => row.getValue("role"),
        header: () => t`Role`,
        id: "role-action",
      }),

      ...(hasUserCompanyDeletePermission
        ? [
            columnHelper.display({
              cell: ({ row }) => (
                <UserCompanyAction
                  userCompanyRelationId={row.getValue("userCompanyRelationId")}
                />
              ),
              header: () => t`Odebrat`,
              id: "user-company-action",
            }),
          ]
        : []),
    ],
    [
      hasCompanyApprovePermission,
      hasCompanyRolePermission,
      hasUserCompanyDeletePermission,
    ],
  );
  const table = useReactTable({
    columns,
    data: companies,
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnVisibility: {
        approvalState: false,
        role: false,
        userCompanyRelationId: false,
      },
    },
  });

  return (
    <>
      <hr className="mt-4" />
      {companies.length > 0 ? (
        <Table>
          <Table.Head table={table} />
          <Table.Body table={table} />
        </Table>
      ) : (
        <Trans>Uživatel není přiřazen k žádné společnosti</Trans>
      )}
    </>
  );
};

const DangerZone = ({ user }: { user: TUser & { blocked: boolean } }) => {
  const { id } = useParams();
  // eslint-disable-next-line lingui/no-unlocalized-strings
  invariant(id, "user id is missing");
  const canDeleteUser = useHasPermission("users:user:delete");
  const deleteUser = useDeleteUserMutation();
  const canBlockUser = useHasPermission("users:user:block");
  const blockUser = useBlockUserMutation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { runWhenConfirmed, dialog, setDialog, rejectRef, resolveRef } =
    useConfirmedAction();

  const handleDeleteUser = () => {
    deleteUser.mutate(
      {
        ssoUserId: user.ssoUserId,
      },
      {
        onError: () => {
          alert(t`Uživatele se nepodařilo smazat`);
        },
        onSuccess: async () => {
          setDialog(undefined);
          await navigate({
            pathname: "..",
            search: location.search,
          });
          void queryClient.invalidateQueries();
        },
      },
    );
  };

  const handleBlockUser = () => {
    blockUser.mutate(
      { blocked: !user.blocked, ssoUserId: user.ssoUserId },
      {
        onError: () => {
          alert(t`Uživatele se nepodařilo zablokovat`);
        },
        onSuccess: async () => {
          setDialog(undefined);
          await navigate({
            pathname: "..",
            search: location.search,
          });
          void queryClient.invalidateQueries();
        },
      },
    );
  };

  return canDeleteUser || canBlockUser ? (
    <>
      <details className="text-red-500">
        <summary>
          <Trans>Nebezpečná zóna</Trans>
        </summary>
        <div className="mt-4 flex flex-col gap-y-4">
          <PermissionGuard permission="users:user:delete">
            <button
              className="rounded-lg border border-red-500 p-2 hover:bg-red-500 hover:text-white"
              onClick={runWhenConfirmed(
                <Trans>
                  Tímto zásahem úplně smažete uživatele {user.name}.
                </Trans>,
                handleDeleteUser,
              )}
            >
              <Trans>Smazat uživatele</Trans>
            </button>
          </PermissionGuard>
          <PermissionGuard permission="users:user:block">
            <button
              className="rounded-lg border border-red-500 p-2 hover:bg-red-500 hover:text-white"
              onClick={runWhenConfirmed(
                user.blocked ? (
                  <Trans>
                    Tímto zásahem odblokujete uživatele {user.name}.
                  </Trans>
                ) : (
                  <Trans>
                    Tímto zásahem zablokujete uživatele {user.name}.
                  </Trans>
                ),
                handleBlockUser,
              )}
            >
              {user.blocked ? (
                <Trans>Odblokovat uživatele</Trans>
              ) : (
                <Trans>Zablokovat uživatele</Trans>
              )}
            </button>
          </PermissionGuard>
        </div>
      </details>
      {dialog ? (
        <Dialog>
          <Dialog.Title>
            <Trans>Opravdu chcete provést akci?</Trans>
          </Dialog.Title>
          <p className="text-red-500">{dialog}</p>
          <Dialog.Actions>
            <Dialog.Confirm
              disabled={
                deleteUser.status === "pending" ||
                blockUser.status === "pending"
              }
              onClick={resolveRef.current}
            >
              <Trans>Ano</Trans>
            </Dialog.Confirm>
            <Dialog.Cancel onClick={rejectRef.current}>
              <Trans>Ne</Trans>
            </Dialog.Cancel>
          </Dialog.Actions>
        </Dialog>
      ) : null}
    </>
  ) : null;
};

export { User };
